import {Container} from '@mui/material';
import React, {useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  getTeamSessionDetailAsync,
  updateTeamSessionAsync,
} from '../../store/teamSessionSlice';
import {
  createSessionVideoSegmentAsync,
  deleteSessionVideoSegmentAsync,
  processSessionVideoAsync,
} from '../../store/sessionVideoSlice';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {createSessionVideoSegmentComplete} from '../../api/sessionVideoAPI';
import {Helmet} from 'react-helmet';
import VideoManagement from '../shared/VideoManagement';
import PageTitle from '../shared/PageTitle';
import BackButton from '../shared/BackButton';
import {useTranslation} from 'react-i18next';
import {VideoTypes} from '../../enums';

interface Props {}

const TeamSessionVideoDetail: React.FC<Props> = () => {
  const {t} = useTranslation();
  const {teamId, teamSessionId: teamSessionIdParam, videoId} = useParams();
  const teamSessionIdInt = parseInt(teamSessionIdParam || '');
  const videoIdInt = parseInt(videoId || '');
  const teamSessionId = useAppSelector(
    state => state.teamSession?.teamSession?.id,
  );
  const teamSessionTimeZone = useAppSelector(
    state => state.teamSession?.teamSession?.timeZone,
  );
  const teamSessionStartTime = useAppSelector(
    state => state.teamSession?.teamSession?.startTime,
  );
  const teamSessionEndTime = useAppSelector(
    state => state.teamSession?.teamSession?.endTime,
  );
  const teamSessionTeamSessionSessionEvents = useAppSelector(
    state => state.teamSession?.teamSession?.teamSessionSessionEvents ?? [],
  );
  const teamSessionPlayerSessionsVideoProcessing = useAppSelector(state =>
    (state.teamSession?.teamSession?.playerSessions ?? []).filter(
      playerSession => !!playerSession.videoProcessingStartedAt,
    ),
  );
  const sessionVideoSegments = useAppSelector(
    state =>
      (state.teamSession?.teamSession?.sessionVideos ?? []).filter(
        sv => sv.id === videoIdInt,
      )[0]?.sessionVideoSegments ?? [],
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getTeamSessionDetailAsync(teamSessionIdInt));
  }, [dispatch, teamSessionIdInt]);

  const title = useMemo(
    () =>
      t('navigation.pageTitle', {
        pageName: t('pages.teamSessionVideo'),
      }),
    [t],
  );

  if (!teamSessionId) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{t('pages.teamSessionVideo')}</PageTitle>
        <BackButton
          navigationUrl={`/teams/${teamId}/teamsessions/${teamSessionIdInt}`}>
          {t('navigation.back', {pageName: t('pages.teamSessionDetail')})}
        </BackButton>
        <VideoManagement
          timeZone={teamSessionTimeZone}
          onTimeZoneChange={(timeZone: string) => {
            dispatch(
              updateTeamSessionAsync({
                id: teamSessionIdInt,
                params: {
                  timeZone,
                },
              }),
            );
          }}
          isProcessing={teamSessionPlayerSessionsVideoProcessing.length > 0}
          sessionStartTime={teamSessionStartTime}
          sessionEndTime={teamSessionEndTime}
          sessionVideoSegments={sessionVideoSegments}
          sessionEvents={teamSessionTeamSessionSessionEvents ?? []}
          refresh={async () =>
            dispatch(getTeamSessionDetailAsync(teamSessionIdInt))
          }
          createSegment={async (params: {
            fileName?: string;
            parts?: number;
            veoUrl?: string;
            pixellotUrl?: string;
            playsightUrl?: string;
            userSelectedStartTime?: Date;
            userSelectedType?: VideoTypes;
          }) => {
            // unwrap() throws on a rejected thunk so callers can
            // detect segment creation failures instead of receiving
            // the rejected action's error payload as if it succeeded
            const payload = await dispatch(
              createSessionVideoSegmentAsync({
                sessionVideoId: videoIdInt,
                fileName: params.fileName,
                parts: params.parts,
                veoUrl: params.veoUrl,
                pixellotUrl: params.pixellotUrl,
                playsightUrl: params.playsightUrl,
                userSelectedStartTime: params.userSelectedStartTime,
                userSelectedType: params.userSelectedType,
              }),
            ).unwrap();
            return {payload};
          }}
          createSegmentComplete={async (params: {
            uploadId: string;
            key: string;
            parts: object[];
          }) =>
            createSessionVideoSegmentComplete({
              sessionVideoId: videoIdInt,
              uploadId: params.uploadId,
              key: params.key,
              parts: params.parts,
            })
          }
          deleteSegment={async (id: number) =>
            dispatch(
              deleteSessionVideoSegmentAsync({
                sessionVideoSegmentId: id,
              }),
            )
          }
          processVideo={async () =>
            dispatch(
              processSessionVideoAsync({
                sessionVideoId: videoIdInt,
              }),
            )
          }
        />
      </Container>
    </>
  );
};

export default TeamSessionVideoDetail;
